import Settings from '../utils/Settings';

/**
 * Client HTTP centralisé avec gestion de l'authentification (Tokens JWT)
 */
class ApiClient {
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  private get baseUrl(): string {
    return Settings.baseUrl;
  }

  // Permet de s'abonner en attendant que le refresh token soit renouvelé
  private subscribeTokenRefresh(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }

  private onRefreshed(token: string) {
    this.refreshSubscribers.map(cb => cb(token));
    this.refreshSubscribers = [];
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    let accessToken = localStorage.getItem('accessToken');

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      // Si on reçoit un 401 et que ce n'est pas déjà une tentative de refresh ou d'authentification
      if (response.status === 401 && !endpoint.includes('/auth/')) {
        return this.handle401Error<T>(endpoint, options);
      }

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        const errorMsg = (typeof data === 'object' && data !== null && data.message)
          ? data.message
          : (typeof data === 'object' && data !== null && data.title)
            ? data.title
            : (typeof data === 'string' && data.trim().length > 0 ? data : response.statusText);

        const errorObj = new Error(errorMsg);
        (errorObj as any).status = response.status;
        (errorObj as any).data = data;
        (errorObj as any).response = { data, status: response.status };
        throw errorObj;
      }

      return data as T;
    } catch (error) {
      console.error(`API Error on ${endpoint}:`, error);
      throw error;
    }
  }

  private async handle401Error<T>(endpoint: string, options: RequestInit): Promise<T> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      this.forceLogout();
      throw new Error('Non autorisé et aucun refresh token.');
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      try {
        const refreshResponse = await fetch(`${this.baseUrl}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: refreshToken })
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          if (refreshData && refreshData.success && refreshData.accessToken) {
            localStorage.setItem('accessToken', refreshData.accessToken);
            if (refreshData.refreshToken) {
              localStorage.setItem('refreshToken', refreshData.refreshToken);
            }
            this.isRefreshing = false;
            this.onRefreshed(refreshData.accessToken);
          } else {
            this.forceLogout();
            throw new Error('Refresh token expiré ou invalide.');
          }
        } else {
          this.forceLogout();
          throw new Error('Refresh token expiré ou invalide.');
        }
      } catch (err) {
        this.forceLogout();
        throw err;
      }
    }

    // Attendre que le token soit rafraîchi par la première requête en file d'attente
    return new Promise<T>((resolve, reject) => {
      this.subscribeTokenRefresh((newToken: string) => {
        if (!newToken) {
          reject(new Error('Session expirée.'));
          return;
        }
        const newOptions = { ...options };
        newOptions.headers = {
          ...newOptions.headers,
          'Authorization': `Bearer ${newToken}`
        };
        resolve(this.request<T>(endpoint, newOptions));
      });
    });
  }

  private forceLogout() {
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('docarn_current_user');
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }

  public async getBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    const accessToken = localStorage.getItem('accessToken');

    const config: RequestInit = {
      ...options,
      method: 'GET',
      headers: {
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        throw {
          status: response.status,
          message: response.statusText
        };
      }
      return await response.blob();
    } catch (error) {
      console.error(`API Error on getBlob ${endpoint}:`, error);
      throw error;
    }
  }

  public async getArrayBuffer(endpoint: string, options: RequestInit = {}): Promise<Uint8Array> {
    const url = `${this.baseUrl}${endpoint}`;
    const accessToken = localStorage.getItem('accessToken');

    const config: RequestInit = {
      ...options,
      method: 'GET',
      headers: {
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        if (response.status === 403) {
          window.dispatchEvent(new CustomEvent('docarn:consultation-limit'));
        }
        throw {
          status: response.status,
          message: response.statusText
        };
      }
      const buffer = await response.arrayBuffer();
      return new Uint8Array(buffer);
    } catch (error) {
      console.error(`API Error on getArrayBuffer ${endpoint}:`, error);
      throw error;
    }
  }

  public get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  public post<T>(endpoint: string, body: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  public put<T>(endpoint: string, body: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  public delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  public async postForm<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    let accessToken = localStorage.getItem('accessToken');
    const headers: Record<string, string> = {};
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      const errorMsg = (typeof data === 'object' && data !== null && (data.message || data.Error || data.error || data.title))
        ? (data.message || data.Error || data.error || data.title)
        : (typeof data === 'string' && data.trim().length > 0 ? data : response.statusText);

      const errorObj = new Error(errorMsg);
      (errorObj as any).data = data;
      throw errorObj;
    }

    return data as T;
  }

  public async putForm<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    let accessToken = localStorage.getItem('accessToken');
    const headers: Record<string, string> = {};
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: formData,
    });

    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      const errorMsg = (typeof data === 'object' && data !== null && (data.message || data.Error || data.error || data.title))
        ? (data.message || data.Error || data.error || data.title)
        : (typeof data === 'string' && data.trim().length > 0 ? data : response.statusText);

      const errorObj = new Error(errorMsg);
      (errorObj as any).data = data;
      throw errorObj;
    }

    return data as T;
  }

  public getBaseUrl(): string {
    return this.baseUrl || '';
  }
}

// Instance singleton
const apiClient = new ApiClient();
export default apiClient;